// src/pages/CreateListingPage.tsx

import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, SubmitHandler } from 'react-hook-form';
import { AnimatePresence } from 'framer-motion';
import { Listing } from '../../components/property/property-card/PropertyCardTypes';
import { useAuth } from '../../auth/firebaseAuth';
import EmailVerificationRequired from '../../components/email-verification/EmailVerificationRequired';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { propertyTemplates } from '../../data/propertyTemplates';
import IdentityVerificationForm from '../../components/identity-verification-form/IdentityVerificationForm';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../analytics/firebaseAnalytics';
import { Helmet } from 'react-helmet';

// Import extracted components
import {
  BasicInformation,
  PropertyDetails,
  DescriptionAmenities,
  AdditionalInformation,
  AccessibilityStep,
  MediaUpload,
  ListingReview,
  ListingTypeSelector,
} from './components';

// Import types and hooks
import { ListingFormData, ListingType, ListingIntent } from './types';
import { usePreviewImages } from './hooks/usePreviewImages';
import {
  useListingForm,
  useImageHandling,
  useVerificationCheck,
  useDraftLoading,
  useListingSubmission,
} from './hooks';

interface CreateListingPageProps {
  draftListing?: Listing;
}

const CreateListingPage: React.FC<CreateListingPageProps> = ({ draftListing }) => {
  const { userProfile, currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();

  // Component state
  const [listingType, setListingType] = useState<ListingType>(null);
  const [step, setStep] = useState(0);
  const [showTemplates, setShowTemplates] = useState(false);
  const [listingIntent, setListingIntent] = useState<ListingIntent>('rent');
  const templateSectionRef = useRef<HTMLDivElement>(null);
  const [locationInput, setLocationInput] = useState('');
  const [existingDraftId, setExistingDraftId] = useState<number | null>(null);
  const [isTemplateWorkflow, setIsTemplateWorkflow] = useState<boolean>(false);

  // Initialize form with custom hook
  const form = useListingForm();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
    setValue,
    clearErrors,
    setError,
  } = form;

  // Use image handling hook (manages internal state)
  const {
    handleImageChange: handleImageChangeHook,
    handleImageRemove: handleImageRemoveHook,
    storedImages,
    prefilledImages,
    setPrefilledImages,
  } = useImageHandling();

  // Wrap image handlers to match MediaUpload component interface
  const handleImageChange = (files: FileList | null) => {
    return handleImageChangeHook(files, setError, clearErrors);
  };

  const handleImageRemove = (index: number) => {
    return handleImageRemoveHook(index, setValue, setError, clearErrors);
  };

  // Preview URLs for image display
  const previewUrls = usePreviewImages(storedImages);

  // Use verification check hook
  const { isVerified } = useVerificationCheck(currentUser);

  // Use submission hook for handling submission and draft saving
  const {
    handleSubmit: handleListingSubmit,
    handleSaveAsDraft,
    isSubmitting: isSubmittingListing,
    submitError: submissionError,
  } = useListingSubmission(currentUser, userProfile, draftListing);

  // Use submission state directly from hook
  const isSubmitting = isSubmittingListing;
  const submitError = submissionError;

  analytics.trackPixelPageView({
    email: currentUser?.email || undefined,
    externalId: currentUser?.uid,
  });

  // Scroll to top when component mounts or step changes
  useEffect(() => {
    // Use setTimeout to ensure content is rendered before scrolling
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [step]);

  // Additional scroll effect when listing type changes (form becomes visible)
  useEffect(() => {
    if (listingType !== null) {
      // Wait a bit longer for animations and form rendering
      const timeoutId = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [listingType]);

  const applyTemplate = (templateId: string) => {
    const template = propertyTemplates[templateId];

    // Track template selection
    analytics.logEvent(AnalyticsEvents.CHOOSE_A_TEMPLATE, {
      template_id: templateId,
      template_name: template.title,
      template_type: template.type,
      page: 'create_listing',
      user_id: currentUser?.uid || 'guest',
    });

    // Set form values based on template
    setValue('title', template.title);
    setValue('type', template.type);
    setValue('description', template.description);
    setValue('bedrooms', template.bedrooms);
    setValue('bathrooms', template.bathrooms);
    setValue('amenities', template.amenities);
    setValue('size', template.size);

    // Mark this as a template workflow
    setIsTemplateWorkflow(true);

    // Move to the first step of single listing form
    setListingType('single');
    setStep(1);
  };

  // Use draft loading hook for both draft and prefill data
  useDraftLoading(
    draftListing,
    setValue,
    clearErrors,
    setLocationInput,
    setStep,
    setExistingDraftId,
    setPrefilledImages,
    setListingType
  );

  const handleTemplateClick = () => {
    // Track start with template
    analytics.logEvent(AnalyticsEvents.START_WITH_TEMPLATE, {
      page: 'create_listing',
      timestamp: new Date().toISOString(),
      user_id: currentUser?.uid || 'guest',
    });

    setShowTemplates(true);
    // Wait for the template section to render before scrolling
    setTimeout(() => {
      templateSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100); // Small delay to ensure the section is rendered
  };

  const handleSaveAsDraftClick = async () => {
    // Only allow logged-in users to save drafts
    if (!currentUser) {
      return;
    }

    const data = watch(); // Get current form values

    // Use the hook's handleSaveAsDraft which includes all validation and save logic
    await handleSaveAsDraft(
      data,
      locationInput,
      listingIntent,
      existingDraftId,
      storedImages,
      prefilledImages,
      setError
    );
  };

  const onSubmit: SubmitHandler<ListingFormData> = async (data) => {
    // Use the hook's handleSubmit which includes all validation and submission logic
    await handleListingSubmit(
      data,
      locationInput,
      listingIntent,
      existingDraftId,
      storedImages,
      prefilledImages,
      setError
    );
  };

  const nextStep = async () => {
    // Ensure location is required
    let valid = false;
    if (listingType === 'single') {
      switch (step) {
        case 1:
          valid = await trigger(['title', 'type']);
          if (valid) {
            // Use different event based on workflow type
            const event = isTemplateWorkflow
              ? AnalyticsEvents.CREATE_LISTING_SECTION_DETAILS
              : AnalyticsEvents.ENTER_LISTING_SECTION_DETAILS;

            analytics.logEvent(event, {
              listing_type: listingType,
              title: watch('title'),
              type: watch('type'),
              workflow: isTemplateWorkflow ? 'template' : 'custom',
              user_id: currentUser?.uid || 'guest',
            });
          }
          break;
        case 2:
          if (!locationInput.trim()) {
            setError('location', {
              type: 'manual',
              message: 'Location is required',
            });
            valid = false;
          } else {
            valid = await trigger(['price', 'location', 'bedrooms', 'bathrooms']);
            if (valid) {
              // Use different event based on workflow type
              const event = isTemplateWorkflow
                ? AnalyticsEvents.ENTER_PROPERTY_DETAILS
                : AnalyticsEvents.ENTER_PROPERTY_DETAILS_CUSTOM;

              analytics.logEvent(event, {
                listing_type: listingType,
                location: locationInput,
                price: watch('price'),
                bedrooms: watch('bedrooms'),
                bathrooms: watch('bathrooms'),
                workflow: isTemplateWorkflow ? 'template' : 'custom',
                user_id: currentUser?.uid || 'guest',
              });
            }
          }
          break;
        case 3:
          valid = await trigger(['description']);
          if (valid) {
            // Use different event based on workflow type
            const event = isTemplateWorkflow
              ? AnalyticsEvents.ENTER_PROPERTY_DESCRIPTION_AMENITIES
              : AnalyticsEvents.ENTER_PROPERTY_DESCRIPTION_AMENITIES_CUSTOM;

            analytics.logEvent(event, {
              listing_type: listingType,
              description_length: watch('description')?.length || 0,
              amenities_count: watch('amenities')?.length || 0,
              workflow: isTemplateWorkflow ? 'template' : 'custom',
              user_id: currentUser?.uid || 'guest',
            });
          }
          break;
        case 4:
          valid = await trigger(['availableFrom', 'berRating']);
          if (valid) {
            // Use different event based on workflow type
            const event = isTemplateWorkflow
              ? AnalyticsEvents.PROPERTY_ADDITIONAL_INFORMATION
              : AnalyticsEvents.PROPERTY_ADDITIONAL_INFORMATION_CUSTOM;

            analytics.logEvent(event, {
              listing_type: listingType,
              ber_rating: watch('berRating'),
              has_virtual_tour: !!watch('virtualTourUrl'),
              workflow: isTemplateWorkflow ? 'template' : 'custom',
              user_id: currentUser?.uid || 'guest',
            });
          }
          break;
        case 5:
          // Accessibility step - no validation required (all fields optional)
          valid = true;
          break;
        case 6:
          if ((!storedImages || storedImages.length === 0) && prefilledImages.length === 0) {
            setError('images', {
              type: 'manual',
              message: 'At least one image is required',
            });
            valid = false;
          } else {
            // If we have prefilled images but no new images, we don't need to trigger validation on 'images' field
            // as it might be empty and cause validation error if required
            if (storedImages && storedImages.length > 0) {
              valid = await trigger(['images']);
            } else {
              valid = true;
              clearErrors('images');
            }

            if (valid) {
              // Use different event based on workflow type
              const event = isTemplateWorkflow
                ? AnalyticsEvents.ADD_MEDIA
                : AnalyticsEvents.ADD_MEDIA_CUSTOM;

              analytics.logEvent(event, {
                listing_type: listingType,
                images_count: (storedImages?.length || 0) + prefilledImages.length,
                has_video: !!watch('videoUrl'),
                workflow: isTemplateWorkflow ? 'template' : 'custom',
                user_id: currentUser?.uid || 'guest',
              });
            }
          }
          break;
        default:
          valid = true;
      }
    }

    if (valid) {
      // Track review step when moving to final step (step 7 for single listing)
      if (listingType === 'single' && step === 6) {
        // Use different event based on workflow type
        const event = isTemplateWorkflow
          ? AnalyticsEvents.REVIEW_LISTING
          : AnalyticsEvents.REVIEW_LISTING_CUSTOM;

        analytics.logEvent(event, {
          listing_type: listingType,
          listing_intent: listingIntent,
          workflow: isTemplateWorkflow ? 'template' : 'custom',
          user_id: currentUser?.uid || 'guest',
        });
      }
      setStep(step + 1);
    }
  };

  const prevStep = () => setStep(step - 1);

  const renderStep = () => {
    if (step === 0) {
      return (
        <ListingTypeSelector
          onTemplateClick={handleTemplateClick}
          onSingleListingClick={() => {
            analytics.logEvent(AnalyticsEvents.CREATE_YOUR_LISTING, {
              page: 'create_listing',
              user_id: currentUser?.uid || 'guest',
            });
            setListingType('single');
            setStep(1);
          }}
          showTemplates={showTemplates}
          templateSectionRef={templateSectionRef}
          onApplyTemplate={applyTemplate}
          setShowTemplates={setShowTemplates}
        />
      );
    }

    if (!listingType) {
      // This should not happen as step > 0 implies listingType is selected
      return <div className="text-center text-red-500">Error: Listing type not selected.</div>;
    }

    if (listingType === 'single') {
      switch (step) {
        case 1:
          return (
            <BasicInformation
              register={register}
              errors={errors}
              watch={watch}
              listingIntent={listingIntent}
              setListingIntent={setListingIntent}
            />
          );
        case 2:
          return (
            <PropertyDetails
              register={register}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              setError={setError}
              watch={watch}
              listingIntent={listingIntent}
              locationInput={locationInput}
              setLocationInput={setLocationInput}
            />
          );
        case 3:
          return <DescriptionAmenities register={register} errors={errors} watch={watch} />;
        case 4:
          return <AdditionalInformation register={register} control={control} errors={errors} />;
        case 5:
          return <AccessibilityStep register={register} errors={errors} />;
        case 6:
          return (
            <MediaUpload
              register={register}
              errors={errors}
              storedImages={storedImages}
              onImageChange={handleImageChange}
              onImageRemove={handleImageRemove}
              previewUrls={[...prefilledImages, ...previewUrls]}
            />
          );
        case 7:
          return (
            <ListingReview
              watch={watch}
              locationInput={locationInput}
              previewUrls={[...prefilledImages, ...previewUrls]}
              setStep={setStep}
              listingIntent={listingIntent}
            />
          );
        default:
          return null;
      }
    }
  };

  // Only check email verification for logged-in users
  if (
    currentUser &&
    remoteConfig.requireEmailVerificationToCreateListing() &&
    userProfile &&
    !userProfile.accountVerification.emailVerified
  ) {
    return (
      <EmailVerificationRequired
        userEmail={userProfile.email}
        userId={userProfile.id}
        userName={userProfile.name}
      />
    );
  }

  // Only check identity verification for logged-in users
  if (currentUser && !isVerified && remoteConfig.requireIdentityVerification()) {
    return <IdentityVerificationForm />;
  }

  // SEO Helmet block
  const seoTitle = 'Create Property Listing | List Your Rental Free | RentSmart.ie';
  const seoDescription =
    'Create your property listing for free on RentSmart.ie. Fast, easy multi-step form for landlords and agents. Reach quality Irish tenants and maximize rental income.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="create listing, property listing, landlord, agent, rent, Ireland, Dublin, Cork, Galway, rental form, RentSmart"
        />
        <link rel="canonical" href="https://rentsmart.ie/create-listing" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/create-listing" />
      </Helmet>
      <div className="py-6 md:py-12">
        <div className="container mx-auto px-2 sm:px-4 max-w-3xl">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-800 mb-4 md:mb-8 text-center px-2">
            Ready to List Your Property? Start Now – It’s Free!
          </h1>

          {/* Notice banner for non-logged-in users */}
          {!currentUser && step > 0 && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    You'll need to create a <strong>free account</strong> after completing your
                    listing to make it live.
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentUser && step > 0 && (
            <p className="text-center text-sm text-gray-500 mb-4">
              Don't have all the information? You can save your progress as a draft.
            </p>
          )}

          {step > 0 && (
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                {listingType === 'single'
                  ? [1, 2, 3, 4, 5, 6, 7].map((s) => (
                      <div key={s} className="flex-1">
                        <button
                          type="button"
                          onClick={() => s <= step && setStep(s)}
                          disabled={s > step}
                          className={`w-8 h-8 md:w-10 md:h-10 mx-auto rounded-full text-sm md:text-lg font-semibold flex items-center justify-center transition-all duration-300 ${
                            s === step
                              ? 'bg-blue-500 text-white'
                              : s < step
                                ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          } ${s <= step ? 'transform hover:scale-110' : ''}`}
                        >
                          {s}
                        </button>
                        {s < 7 && (
                          <div
                            className={`h-0.5 md:h-1 mx-auto w-full ${
                              s < step ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          ></div>
                        )}
                      </div>
                    ))
                  : [1, 2].map((s) => (
                      <div key={s} className="flex-1">
                        <button
                          type="button"
                          onClick={() => s <= step && setStep(s)}
                          disabled={s > step}
                          className={`w-8 h-8 md:w-10 md:h-10 mx-auto rounded-full text-sm md:text-lg font-semibold flex items-center justify-center transition-all duration-300 ${
                            s === step
                              ? 'bg-blue-500 text-white'
                              : s < step
                                ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          } ${s <= step ? 'transform hover:scale-110' : ''}`}
                        >
                          {s}
                        </button>
                        {s < 2 && (
                          <div
                            className={`h-0.5 md:h-1 mx-auto w-full ${
                              s < step ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          ></div>
                        )}
                      </div>
                    ))}
              </div>
              <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-600 px-1">
                {listingType === 'single' ? (
                  <>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center">Basic</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center">Details</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center hidden xs:inline sm:inline">
                      Description
                    </span>
                    <span className="xs:hidden sm:hidden text-center">Desc</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center hidden xs:inline sm:inline">
                      Additional
                    </span>
                    <span className="xs:hidden sm:hidden text-center">Add</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center hidden xs:inline sm:inline">
                      Accessibility
                    </span>
                    <span className="xs:hidden sm:hidden text-center">Access</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center">Media</span>
                    <span className="truncate max-w-[60px] sm:max-w-none text-center">Review</span>
                  </>
                ) : (
                  <>
                    <span className="truncate max-w-[120px] sm:max-w-none text-center">
                      Group Details
                    </span>
                    <span className="truncate max-w-[120px] sm:max-w-none text-center">Units</span>
                  </>
                )}
              </div>
            </div>
          )}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-white shadow-md rounded-lg p-4 sm:p-6 md:p-8"
          >
            <AnimatePresence mode="wait">
              <>{renderStep()}</>
            </AnimatePresence>
            <div className="mt-6 md:mt-8 flex flex-wrap justify-between items-center gap-3">
              {step > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-400 transition duration-300"
                >
                  Previous
                </button>
              )}

              <div className="flex-grow flex justify-end flex-wrap gap-3">
                {listingType === 'single' && step < 7 && (
                  <>
                    {currentUser && listingType === 'single' && step >= 3 && (
                      <button
                        type="button"
                        onClick={handleSaveAsDraftClick}
                        disabled={isSubmitting}
                        className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition duration-300 border border-gray-300 ${
                          isSubmitting
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        title="Save your progress and complete later"
                      >
                        Save as Draft
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={nextStep}
                      className="bg-blue-500 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-blue-600 transition duration-300"
                    >
                      Next
                    </button>
                  </>
                )}

                {listingType === 'single' && step === 7 && (
                  <>
                    {currentUser && (
                      <button
                        type="button"
                        onClick={handleSaveAsDraftClick}
                        disabled={isSubmitting}
                        className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition duration-300 border border-gray-300 ${
                          isSubmitting
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        title="Save your progress and complete later"
                      >
                        Save as Draft
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition duration-300 relative ${
                        isSubmitting
                          ? 'bg-green-400 cursor-not-allowed'
                          : 'bg-green-500 hover:bg-green-600'
                      } text-white`}
                    >
                      <span className={`${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                        Create Listing
                      </span>
                      {isSubmitting && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>

              {submitError && (
                <div className="w-full mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateListingPage;
